import { Cell, Row } from "@tanstack/react-table";
import { VirtualizedRowProps } from "./VirtualizedRow";
import { VirtualItem, Virtualizer } from "@tanstack/react-virtual";
import { ReactNode, useLayoutEffect, useRef } from "react";

interface VirtualizedBodyRowProps extends Omit<VirtualizedRowProps, "children"> {
	row: Row<any>;
	rowHeight: number;
	onRowHeightChange: (rowId: string, measuredHeight: number) => number;
	rowVirtualizer: Virtualizer<HTMLDivElement, HTMLTableRowElement>;
	virtualRow: VirtualItem;
	children: (virtualItems: VirtualItem[], cells: Cell<any, unknown>[]) => ReactNode;
}

export default function VirtualizedBodyRow({
	row,
	rowHeight,
	onRowHeightChange,
	rowVirtualizer,
	virtualRow,
	columnVirtualizer,
	virtualPaddingLeft,
	virtualPaddingRight,
	children,
}: VirtualizedBodyRowProps) {
	const visibleCells = row.getVisibleCells();
	const virtualColumns = columnVirtualizer.getVirtualItems();
	const rowRef = useRef<HTMLTableRowElement | null>(null);

	useLayoutEffect(() => {
		const element = rowRef.current;
		if (!element) return;

		const measure = () => {
			onRowHeightChange(row.id, element.getBoundingClientRect().height);
		};

		measure();
	}, [onRowHeightChange, row.id]);

	return (
		<tr
			data-index={virtualRow.index} // NB: needed for dynamic row height measurement
			key={row.id}
			style={{
				display: "flex",
				position: "absolute",
				top: 0,
				left: 0,
				transform: `translateY(${virtualRow.start}px)`, // NB: this should always be a `style` as it changes on scroll
				minHeight: `${rowHeight}px`,
			}}
			ref={(element) => {
				rowRef.current = element;
				rowVirtualizer.measureElement(element);
			}}
		>
			{typeof virtualPaddingLeft === "number" && virtualPaddingLeft > 0 ? (
				// NB: fake empty column to the left for virtualization scroll padding
				<td style={{ display: "flex", width: virtualPaddingLeft }} />
			) : null}
			{children(virtualColumns, visibleCells)}
			{typeof virtualPaddingRight === "number" && virtualPaddingRight > 0 ? (
				// NB: fake empty column to the right for virtualization scroll padding
				<td style={{ display: "flex", width: virtualPaddingRight }} />
			) : null}
		</tr>
	);
}
